import type { Point } from "@labeling-tools/ui-components/src/ImageWithBoundingBox";
import { orderCorners } from "@labeling-tools/ui-components/src/utils/obbGeometry";
import { MIN_NORMALIZED_SIZE, pointsToGeom } from "./box-drawing-utils";

/**
 * Moving and resizing a box in its own frame, so a tilted box (SAM 2 draws
 * shelf gaps as rotated rectangles) keeps its angle. A labeler's box has no
 * tilt, so for it this is the plain axis-aligned move/resize.
 *
 * Points are normalized (0-1) on the image. A rotated rectangle's corners are
 * only square in pixels, not in normalized space unless the image is square,
 * so the resize works in the image's on-screen pixels (`FrameSize`).
 */

/** The image as drawn on screen. Only its aspect ratio matters. */
export interface FrameSize {
  width: number;
  height: number;
}

/** A box corner, as its index in `cornerRing`. */
export type CornerIndex = 0 | 1 | 2 | 3;

/**
 * A box's corners in ring order, whatever order they're stored in, so a
 * handle's index names the same corner for the whole drag. An axis-aligned
 * box comes out top-left, top-right, bottom-right, bottom-left.
 */
export const cornerRing = (points: number[][]): Point[] =>
  orderCorners(points as Point[]);

const sub = (a: Point, b: Point): Point => [a[0] - b[0], a[1] - b[1]];
const dot = (a: Point, b: Point) => a[0] * b[0] + a[1] * b[1];

/**
 * The box with one corner dragged to `to` (normalized): the opposite corner
 * stays put and the box keeps its angle, so only its width and height change.
 * Dragging past the opposite corner mirrors the box rather than inverting it.
 *
 * The axes are the box's first edge and the line square to it. A stored quad
 * that's slightly off-square comes out as the rectangle on those axes.
 */
export const resizeFromCorner = (
  points: number[][],
  corner: CornerIndex,
  to: Point,
  frame: FrameSize,
): number[][] => {
  const { width: w, height: h } = frame;
  const toPx = ([x, y]: Point): Point => [x * w, y * h];
  const ring = cornerRing(points).map(toPx);
  const anchor = ring[(corner + 2) % 4];

  const edge = sub(ring[1], ring[0]);
  const length = Math.hypot(edge[0], edge[1]);
  // No edge to take an angle from: a collapsed box resizes axis-aligned.
  const u: Point = length > 0 ? [edge[0] / length, edge[1] / length] : [1, 0];
  const v: Point = [-u[1], u[0]];

  const d = sub(toPx(to), anchor);
  const du = dot(d, u);
  const dv = dot(d, v);
  const at = (a: number, b: number): Point => [
    (anchor[0] + a * u[0] + b * v[0]) / w,
    (anchor[1] + a * u[1] + b * v[1]) / h,
  ];
  return cornerRing([at(0, 0), at(du, 0), at(du, dv), at(0, dv)]);
};

/**
 * The box shifted by (dx, dy), normalized, keeping its shape. The shift is
 * held so the box doesn't leave the image; a box that already pokes past an
 * edge (a mask can) isn't pushed further out, and isn't snapped back either.
 */
export const translateBox = (
  points: number[][],
  dx: number,
  dy: number,
): number[][] => {
  const { minX, minY, maxX, maxY } = pointsToGeom(points);
  const clamp = (v: number, lo: number, hi: number) =>
    Math.min(hi, Math.max(lo, v));
  const x = clamp(dx, Math.min(0, -minX), Math.max(0, 1 - maxX));
  const y = clamp(dy, Math.min(0, -minY), Math.max(0, 1 - maxY));
  return points.map(([px, py]) => [px + x, py + y]);
};

/**
 * True when a side of the box is too short to keep: a resize that collapses
 * the box is dropped rather than saved.
 */
export const isTooSmall = (points: number[][], frame: FrameSize): boolean => {
  const ring = cornerRing(points).map(
    ([x, y]): Point => [x * frame.width, y * frame.height],
  );
  const side = (a: Point, b: Point) => Math.hypot(a[0] - b[0], a[1] - b[1]);
  const min = MIN_NORMALIZED_SIZE * Math.min(frame.width, frame.height);
  return side(ring[0], ring[1]) < min || side(ring[1], ring[2]) < min;
};
